/**
 * UserMenu — Wave A6 Chunk 2 (Task 2.4, closes audit G1/G2).
 *
 * The top-right avatar becomes a real account menu: name + email header,
 * Profile & Settings, Appearance, and Sign Out — so signing out is ≤2 clicks
 * from anywhere. Renders for ALL roles. Full keyboard + ARIA menu support.
 *
 * Styling uses @kantera/ui theme tokens only (no Tailwind, Law 6).
 */

import React, { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  css,
  flexRow,
  flexCol,
  theme,
  glassSurface,

  useTranslationTyped,
} from '@kantera/ui';
import { useSignOut } from '../hooks/useSignOut';

interface UserMenuProps {
  name: string;
  email: string;
  roleLabel: string;
  initials: string;
  avatarUrl: string | null;
  /** Hide the name/role text column (mobile/narrow top bar). */
  compact?: boolean;
}

interface MenuAction {
  key: string;
  label: string;
  onSelect: () => void;
  danger?: boolean;
}

export function UserMenu({ name, email, roleLabel, initials, avatarUrl, compact }: UserMenuProps) {
  const { t } = useTranslationTyped();
  const navigate = useNavigate();
  const signOut = useSignOut();

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();

  const actions: MenuAction[] = [
    {
      key: 'settings',
      label: t('userMenu.profileSettings'),
      onSelect: () => navigate('/settings'),
    },
    {
      key: 'appearance',
      label: t('userMenu.appearance'),
      onSelect: () => navigate('/settings/appearance'),
    },
    { key: 'signout', label: t('userMenu.signOut'), onSelect: signOut, danger: true },
  ];

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    setActiveIndex(-1);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  const openMenu = useCallback((focusIndex: number) => {
    setOpen(true);
    setActiveIndex(focusIndex);
  }, []);

  // Move focus to the active item whenever it changes while open.
  useEffect(() => {
    if (open && activeIndex >= 0) {
      itemRefs.current[activeIndex]?.focus();
    }
  }, [open, activeIndex]);

  // Click-outside closes (no focus return — the user clicked elsewhere).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, close]);

  const onButtonKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openMenu(0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu(actions.length - 1);
    }
  };

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % actions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + actions.length) % actions.length);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActiveIndex(actions.length - 1);
    } else if (e.key === 'Tab') {
      close(false);
    }
  };

  const select = (action: MenuAction) => {
    close(false);
    action.onSelect();
  };

  const avatarStyle: CSSProperties = {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    background: avatarUrl ? `center / cover no-repeat url(${avatarUrl})` : 'var(--k-brand)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 700,
    fontFamily: 'var(--k-font-display)',
    flexShrink: 0,
    letterSpacing: '0.03em',
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={t('userMenu.openMenu', { name: name || email })}
        onClick={() => (open ? close(false) : openMenu(-1))}
        onKeyDown={onButtonKeyDown}
        style={
          css(flexRow('10px'), {
            alignItems: 'center',
            paddingLeft: '12px',
            borderLeft: '1px solid var(--k-border)',
            background: 'transparent',
            border: 'none',
            borderLeftWidth: '1px',
            borderLeftStyle: 'solid',
            borderLeftColor: 'var(--k-border)',
            cursor: 'pointer',
            font: 'inherit',
            color: 'inherit',
            // Keyboard focus is handled by the global :focus-visible rule in
            // index.css. focusRing() must NOT go in the base inline style — an
            // inline style has no :focus selector, so it paints the ring
            // permanently (a persistent accent-coloured box around the name,
            // red in light mode).
          }) as CSSProperties
        }
      >
        <span style={avatarStyle} aria-hidden="true">
          {avatarUrl ? '' : initials}
        </span>
        {!compact && (
          <span style={{ lineHeight: 1.2, textAlign: 'left' }}>
            {name && (
              <span
                style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'var(--k-text-primary)',
                  whiteSpace: 'nowrap',
                }}
              >
                {name}
              </span>
            )}
            {roleLabel && (
              <span
                style={{ display: 'block', fontSize: '11px', color: 'var(--k-text-secondary)' }}
              >
                {roleLabel}
              </span>
            )}
          </span>
        )}
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={t('userMenu.menuLabel')}
          onKeyDown={onMenuKeyDown}
          style={
            css(glassSurface(), flexCol(0), {
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              minWidth: '240px',
              padding: theme.spacing.xs,
              borderRadius: theme.radius.md,
              zIndex: theme.zIndex.dropdown,
              boxShadow: theme.shadows.lg,
            }) as CSSProperties
          }
        >
          {/* Header — name + email (non-interactive) */}
          <div
            style={css(flexCol('2px'), {
              padding: `${theme.spacing.sm} ${theme.spacing.md}`,
              borderBottom: `1px solid ${theme.colors.border.primary}`,
              marginBottom: theme.spacing.xs,
            })}
          >
            <span style={{ fontWeight: 700, color: theme.colors.text.primary, fontSize: '14px' }}>
              {name || t('userMenu.noName')}
            </span>
            <span style={{ color: theme.colors.text.muted, fontSize: '12px' }}>{email}</span>
          </div>

          {actions.map((action, i) => (
            <button
              key={action.key}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              type="button"
              role="menuitem"
              tabIndex={activeIndex === i ? 0 : -1}
              onClick={() => select(action)}
              onMouseEnter={() => setActiveIndex(i)}
              style={
                css(flexRow(theme.spacing.sm), {
                  width: '100%',
                  alignItems: 'center',
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  background: activeIndex === i ? theme.colors.bg.tertiary : 'transparent',
                  border: 'none',
                  borderRadius: theme.radius.sm,
                  cursor: 'pointer',
                  font: 'inherit',
                  fontSize: '13px',
                  textAlign: 'left',
                  color: action.danger ? theme.colors.danger.primary : theme.colors.text.secondary,
                  // See note on the trigger button: keyboard focus comes from
                  // the global :focus-visible rule, not an always-on inline ring.
                }) as CSSProperties
              }
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
